import { create } from "zustand";
import type {
  DiscussionTab,
  ChatMember,
  ChatMessage,
  ChatAttachment,
  Discussion,
} from "../types/messaging";
import type { User } from "../types/user";
import { messagerieApi } from "../services/api/messagerie";
import { useAuthStore } from "./auth";
import { useDataStore } from "./user_org";
import { useApiConfigStore } from "./api_config";

// --- Helpers ---

/** Map a backend User object to a lightweight ChatMember for the UI */
function userToChatMember(user: User): ChatMember {
  return {
    id: user.id,
    name: user.infos?.display_name || user.infos?.full_name || "Inconnu",
    role: user.role || "",
    online: user.activity?.connected ?? false,
  };
}

/** Format an ISO timestamp to a readable string like "14:30" or "14:30 Hier" */
function formatTimestamp(iso: string | undefined): string {
  if (!iso) return "";
  try {
    const date = new Date(iso);
    const now = new Date();
    const time = date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday =
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear();

    if (isToday) return time;
    if (isYesterday) return `${time} Hier`;
    return `${time} ${date.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}`;
  } catch {
    return iso;
  }
}

/** Normalise a backend ChatMessage so legacy camelCase fields are populated */
function normalizeMessage(msg: ChatMessage): ChatMessage {
  const rawTimestamp = msg.created_at || msg.timestamp;
  return {
    ...msg,
    senderId: msg.sender_id || msg.senderId,
    timestamp: formatTimestamp(rawTimestamp),
    replyToId: msg.reply_to_id || msg.replyToId,
    reactions: (msg.reactions || []).map((r) => ({
      ...r,
      userId: r.user_id || r.userId,
    })),
  };
}

/** Normalise a backend Discussion so legacy fields are populated */
function normalizeDiscussion(d: Discussion): Discussion {
  const lm = d.last_message;
  return {
    ...d,
    lastMessage:
      d.lastMessage ??
      (lm
        ? lm.attachment_type
          ? lm.attachment_type === "image"
            ? "Image"
            : "Fichier"
          : lm.text || ""
        : ""),
    lastMessageTime:
      d.lastMessageTime ?? formatTimestamp(lm?.timestamp),
    unread: d.unread ?? (d.unread_count != null ? d.unread_count > 0 : false),
  };
}

// --- Store ---

interface MessagerieStore {
  // State
  currentUserId: string;
  members: ChatMember[];
  discussions: Discussion[];
  messages: Record<string, ChatMessage[]>;
  activeTab: DiscussionTab;
  selectedDiscussionId: string | null;
  searchQuery: string;
  replyingToMessageId: string | null;
  unreadCounts: Record<string, number>;

  // Loading / connection
  isLoadingDiscussions: boolean;
  isLoadingMessages: boolean;
  isLoadingOlderMessages: boolean;
  hasMoreMessages: Record<string, boolean>;
  isSending: boolean;
  wsConnected: boolean;
  typingUsers: Record<string, string[]>; // discussionId -> userId[]
  _initialized: boolean;

  // Actions — async
  initGlobal: () => Promise<void>;
  init: () => Promise<void>;
  fetchDiscussions: (tab?: DiscussionTab) => Promise<void>;
  fetchMessages: (discussionId: string) => Promise<void>;
  fetchOlderMessages: (discussionId: string) => Promise<void>;
  connectWS: () => void;
  disconnectWS: () => void;

  // Actions — sync / triggers
  setActiveTab: (tab: DiscussionTab) => void;
  setSelectedDiscussion: (id: string) => void;
  setSearchQuery: (q: string) => void;
  setReplyingTo: (messageId: string | null) => void;

  sendMessage: (
    text: string,
    attachment?: ChatAttachment,
    mentions?: string[],
    file?: File,
  ) => Promise<void>;
  sendMessageTo: (
    discussionId: string,
    text: string,
    replyToId?: string | null,
    attachment?: ChatAttachment,
    mentions?: string[],
    file?: File,
    channel?: import("types/messaging").MessageChannel,
  ) => Promise<void>;
  deleteMessageIn: (discussionId: string, messageId: string) => Promise<void>;
  toggleReactionIn: (discussionId: string, messageId: string, emoji: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  toggleReaction: (messageId: string, emoji: string) => Promise<void>;

  createDiscussion: (
    name: string,
    memberIds: string[],
    tab: DiscussionTab,
  ) => Promise<string | null>;

  sendTyping: () => void;

  // Derived getters
  getFilteredDiscussions: () => Discussion[];
  getUnreadCount: (tab: DiscussionTab) => number;
  getTotalUnreadCount: () => number;
  getSelectedDiscussion: () => Discussion | undefined;
  getDiscussionMessages: () => ChatMessage[];
  getDiscussionMembers: () => ChatMember[];
  getOnlineCount: () => number;
  getMember: (id: string) => ChatMember | undefined;
  getMessageById: (messageId: string) => ChatMessage | undefined;
  getMatchingMembers: (query: string) => ChatMember[];

  // Internal — WS handlers
  _handleWSMessage: (event: MessageEvent) => void;
  _ws: WebSocket | null;
}

export const useMessagerieStore = create<MessagerieStore>((set, get) => ({
  currentUserId: "",
  members: [],
  discussions: [],
  messages: {},
  activeTab: "generales",
  selectedDiscussionId: null,
  searchQuery: "",
  replyingToMessageId: null,
  unreadCounts: {},

  isLoadingDiscussions: false,
  isLoadingMessages: false,
  isLoadingOlderMessages: false,
  hasMoreMessages: {},
  isSending: false,
  wsConnected: false,
  typingUsers: {},
  _initialized: false,

  _ws: null,

  // ─── Global init (lightweight — called from MainLayout) ─────
  // Called every time orgMembers changes; only does real work once.
  initGlobal: async () => {
    const firebaseUser = useAuthStore.getState().user;
    if (!firebaseUser) return;
    const orgMembers = useDataStore.getState().orgMembers || [];
    if (orgMembers.length === 0) return;

    // Always keep userId + members fresh
    set({ currentUserId: firebaseUser.uid, members: orgMembers.map(userToChatMember) });

    // Only fetch unread counts + connect WS once
    if (!get()._initialized) {
      set({ _initialized: true });
      try {
        const counts = await messagerieApi.getUnreadCounts();
        set({ unreadCounts: counts });
      } catch (err) {
        console.error("Failed to fetch unread counts:", err);
      }
      get().connectWS();
    }
  },

  // ─── Full init (called when entering messagerie screen) ─────
  init: async () => {
    const firebaseUser = useAuthStore.getState().user;
    if (!firebaseUser) return;

    // Refresh userId + members
    const orgMembers = useDataStore.getState().orgMembers || [];
    set({ currentUserId: firebaseUser.uid, members: orgMembers.map(userToChatMember) });

    // Connect WS if not already connected
    get().connectWS();

    // Fetch discussions for default tab
    await get().fetchDiscussions();
  },

  // ─── Fetch discussions ───────────────────────────────────────
  fetchDiscussions: async (tab?) => {
    set({ isLoadingDiscussions: true });
    try {
      const raw = await messagerieApi.listDiscussions(
        tab ? { tab } : undefined,
      );
      const { selectedDiscussionId, activeTab } = get();
      const discussions = raw.map((d) => {
        const disc = normalizeDiscussion(d);
        // Keep selected discussion marked as read
        if (disc.id === selectedDiscussionId) {
          disc.unread = false;
          disc.unread_count = 0;
        }
        return disc;
      });

      set({ discussions });

      // Auto-select first discussion if none selected
      if (!selectedDiscussionId) {
        const first = discussions.find((d) => d.tab === (tab || activeTab));
        if (first) {
          get().setSelectedDiscussion(first.id);
        }
      }
    } catch (err) {
      console.error("Failed to fetch discussions:", err);
    } finally {
      set({ isLoadingDiscussions: false });
    }
  },

  // ─── Fetch messages ──────────────────────────────────────────
  fetchMessages: async (discussionId) => {
    set({ isLoadingMessages: true });
    try {
      const raw = await messagerieApi.listMessages(discussionId, {
        limit: 50,
      });
      const messages = raw.map(normalizeMessage);

      set((s) => ({
        messages: { ...s.messages, [discussionId]: messages },
        hasMoreMessages: { ...s.hasMoreMessages, [discussionId]: raw.length === 50 },
      }));

      // Mark as read
      if (messages.length > 0) {
        const lastMsg = messages[messages.length - 1];
        messagerieApi.markAsRead(discussionId, lastMsg.id).catch(() => {});
      }
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    } finally {
      set({ isLoadingMessages: false });
    }
  },

  // ─── Fetch older messages (scroll up pagination) ──────────
  fetchOlderMessages: async (discussionId) => {
    const { isLoadingOlderMessages, hasMoreMessages, messages } = get();
    if (isLoadingOlderMessages || !hasMoreMessages[discussionId]) return;

    const existing = messages[discussionId] || [];
    if (existing.length === 0) return;

    // Use the oldest message id as cursor
    const oldestMsg = existing[0];
    const beforeId = oldestMsg.id;

    set({ isLoadingOlderMessages: true });
    try {
      const raw = await messagerieApi.listMessages(discussionId, {
        limit: 50,
        before: beforeId,
      });
      const older = raw.map(normalizeMessage);

      set((s) => ({
        messages: {
          ...s.messages,
          [discussionId]: [...older, ...(s.messages[discussionId] || [])],
        },
        hasMoreMessages: { ...s.hasMoreMessages, [discussionId]: raw.length === 50 },
      }));
    } catch (err) {
      console.error("Failed to fetch older messages:", err);
    } finally {
      set({ isLoadingOlderMessages: false });
    }
  },

  // ─── WebSocket ───────────────────────────────────────────────
  connectWS: () => {
    const firebaseUser = useAuthStore.getState().user;
    if (!firebaseUser) return;

    // Guard: don't open a second WS
    const existingWs = get()._ws;
    if (existingWs && (existingWs.readyState === WebSocket.OPEN || existingWs.readyState === WebSocket.CONNECTING)) return;

    // Build WS URL from API base
    const apiUrl = useApiConfigStore.getState().getApiUrl();
    const wsBase = apiUrl.replace(/^http/, "ws");

    firebaseUser.getIdToken().then((token) => {
      const ws = new WebSocket(`${wsBase}/messagerie/ws?token=${token}`);
      // Store immediately to prevent duplicate connections
      set({ _ws: ws });

      ws.onopen = () => {
        set({ wsConnected: true });
      };

      ws.onmessage = (event) => {
        get()._handleWSMessage(event);
      };

      ws.onclose = () => {
        set({ wsConnected: false, _ws: null });
        // Reconnect after 3s
        setTimeout(() => {
          if (!get()._ws) get().connectWS();
        }, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    });
  },

  disconnectWS: () => {
    const ws = get()._ws;
    if (ws) {
      ws.close();
      set({ _ws: null, wsConnected: false });
    }
  },

  _handleWSMessage: (event) => {
    try {
      const raw = JSON.parse(event.data);
      const data = raw.data || raw; // payload is nested in { type, data }
      const eventType = raw.type;
      const state = get();

      switch (eventType) {
        case "new_message": {
          const rawMsg = (data.message || data) as ChatMessage;
          if (!rawMsg || !rawMsg.id) break;
          const msg = normalizeMessage(rawMsg);
          const discId = (data.discussion_id || rawMsg.discussion_id) as string;
          if (!discId) break;

          // Append message (skip if already exists — optimistic duplicate)
          const existing = state.messages[discId] || [];
          if (existing.some((m) => m.id === msg.id)) break;

          set((s) => ({
            messages: {
              ...s.messages,
              [discId]: [...(s.messages[discId] || []), msg],
            },
          }));

          // Update discussion preview
          set((s) => ({
            discussions: s.discussions.map((d) =>
              d.id === discId
                ? {
                    ...d,
                    lastMessage: msg.attachment
                      ? msg.attachment.type === "image"
                        ? "Image"
                        : msg.attachment.name || "Fichier"
                      : msg.text?.substring(0, 40) || "",
                    lastMessageTime: msg.timestamp || msg.created_at || "",
                    unread: d.id !== s.selectedDiscussionId,
                  }
                : d,
            ),
          }));

          // Auto mark-as-read if viewing this discussion, otherwise increment unread
          if (state.selectedDiscussionId === discId) {
            messagerieApi.markAsRead(discId, msg.id).catch(() => {});
          } else {
            set((s) => ({
              unreadCounts: {
                ...s.unreadCounts,
                [discId]: (s.unreadCounts[discId] || 0) + 1,
              },
            }));
          }
          break;
        }

        case "message_deleted": {
          const discId = data.discussion_id as string;
          const msgId = data.message_id as string;
          set((s) => ({
            messages: {
              ...s.messages,
              [discId]: (s.messages[discId] || []).map((m) =>
                m.id === msgId
                  ? {
                      ...m,
                      deleted: true,
                      text: undefined,
                      attachment: undefined,
                      reactions: [],
                    }
                  : m,
              ),
            },
          }));
          break;
        }

        case "reaction_updated": {
          const discId = data.discussion_id as string;
          const msgId = data.message_id as string;
          const reactions = (data.reactions || []).map((r: any) => ({
            ...r,
            userId: r.user_id || r.userId,
          }));
          if (!discId || !msgId) break;
          set((s) => ({
            messages: {
              ...s.messages,
              [discId]: (s.messages[discId] || []).map((m) =>
                m.id === msgId ? { ...m, reactions } : m,
              ),
            },
          }));
          break;
        }

        case "typing": {
          const discId = data.discussion_id as string;
          const userId = data.user_id as string;
          if (userId === state.currentUserId) break;

          // Add to typing list
          set((s) => {
            const current = s.typingUsers[discId] || [];
            if (current.includes(userId)) return s;
            return {
              typingUsers: {
                ...s.typingUsers,
                [discId]: [...current, userId],
              },
            };
          });

          // Remove after 3s
          setTimeout(() => {
            set((s) => ({
              typingUsers: {
                ...s.typingUsers,
                [discId]: (s.typingUsers[discId] || []).filter(
                  (id) => id !== userId,
                ),
              },
            }));
          }, 3000);
          break;
        }

        case "discussion_created": {
          const disc = normalizeDiscussion(data.discussion as Discussion);
          set((s) => ({
            discussions: [disc, ...s.discussions],
            messages: { ...s.messages, [disc.id]: [] },
          }));
          break;
        }

        case "members_updated": {
          const discId = data.discussion_id as string;
          const members = data.members as string[];
          set((s) => ({
            discussions: s.discussions.map((d) =>
              d.id === discId ? { ...d, members } : d,
            ),
          }));
          break;
        }
      }
    } catch (err) {
      console.error("WS message parse error:", err);
    }
  },

  // ─── Tab / selection ─────────────────────────────────────────
  setActiveTab: (tab) => {
    set({ activeTab: tab });
    get().fetchDiscussions(tab);
  },

  setSelectedDiscussion: (id) => {
    // Mark as read locally
    const discussions = get().discussions.map((d) =>
      d.id === id ? { ...d, unread: false, unread_count: 0 } : d,
    );
    set({
      selectedDiscussionId: id,
      discussions,
      replyingToMessageId: null,
      unreadCounts: { ...get().unreadCounts, [id]: 0 },
    });

    // Fetch messages if not loaded or refresh
    get().fetchMessages(id);
  },

  setSearchQuery: (q) => set({ searchQuery: q }),

  setReplyingTo: (messageId) => set({ replyingToMessageId: messageId }),

  // ─── Send message ────────────────────────────────────────────
  sendMessage: async (text, attachment, mentions, file) => {
    const { selectedDiscussionId, currentUserId, replyingToMessageId } = get();
    if (!selectedDiscussionId) return;
    if (!text.trim() && !attachment && !file) return;

    set({ isSending: true });

    const now = new Date();
    const timestamp = now.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    // Optimistic message
    const optimisticId = "opt_" + Date.now();
    const optimisticMsg: ChatMessage = {
      id: optimisticId,
      senderId: currentUserId,
      sender_id: currentUserId,
      text: text.trim() || undefined,
      attachment,
      reactions: [],
      timestamp,
      replyToId: replyingToMessageId || undefined,
      reply_to_id: replyingToMessageId || undefined,
      mentions: mentions && mentions.length > 0 ? mentions : undefined,
    };

    // Optimistic insert
    set((s) => ({
      messages: {
        ...s.messages,
        [selectedDiscussionId]: [
          ...(s.messages[selectedDiscussionId] || []),
          optimisticMsg,
        ],
      },
      replyingToMessageId: null,
    }));

    // Update discussion preview optimistically
    const preview = attachment
      ? attachment.type === "image"
        ? "Image"
        : `${attachment.name || "Fichier"}`
      : text.trim().substring(0, 40) + (text.trim().length > 40 ? ".." : "");

    set((s) => ({
      discussions: s.discussions.map((d) =>
        d.id === selectedDiscussionId
          ? { ...d, lastMessage: preview, lastMessageTime: timestamp }
          : d,
      ),
    }));

    try {
      // Upload file if provided
      let finalAttachment = attachment;
      if (file) {
        const uploaded = await messagerieApi.uploadAttachment(
          selectedDiscussionId,
          file,
        );
        finalAttachment = {
          type: file.type.startsWith("image/") ? "image" : "file",
          name: uploaded.name,
          gcs_path: uploaded.gcs_path,
          mime_type: uploaded.mime_type,
          size: uploaded.size,
        };
      }

      const payload: {
        text?: string;
        attachment?: ChatAttachment;
        reply_to_id?: string;
        mentions?: string[];
      } = {};
      if (text.trim()) payload.text = text.trim();
      if (finalAttachment) payload.attachment = finalAttachment;
      if (replyingToMessageId) payload.reply_to_id = replyingToMessageId;
      if (mentions && mentions.length > 0) payload.mentions = mentions;

      const sent = await messagerieApi.sendMessage(
        selectedDiscussionId,
        payload,
      );

      // Replace optimistic message with real one
      const real = normalizeMessage(sent);
      set((s) => ({
        messages: {
          ...s.messages,
          [selectedDiscussionId]: (
            s.messages[selectedDiscussionId] || []
          ).map((m) => (m.id === optimisticId ? real : m)),
        },
      }));
    } catch (err) {
      console.error("Failed to send message:", err);
      // Remove optimistic message on failure
      set((s) => ({
        messages: {
          ...s.messages,
          [selectedDiscussionId]: (
            s.messages[selectedDiscussionId] || []
          ).filter((m) => m.id !== optimisticId),
        },
      }));
    } finally {
      set({ isSending: false });
    }
  },

  // ─── Send message to a specific discussion (without changing global selection) ──
  sendMessageTo: async (discussionId, text, replyToId, attachment, mentions, file, channel) => {
    const { currentUserId } = get();
    if (!discussionId) return;
    if (!text.trim() && !attachment && !file) return;

    set({ isSending: true });

    const now = new Date();
    const timestamp = now.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    const optimisticId = "opt_" + Date.now();
    const optimisticAttachment = attachment
      ? { ...attachment, url: file && file.type.startsWith("image/") ? URL.createObjectURL(file) : attachment.url }
      : undefined;
    const optimisticMsg: ChatMessage = {
      id: optimisticId,
      senderId: currentUserId,
      sender_id: currentUserId,
      text: text.trim() || undefined,
      attachment: optimisticAttachment,
      reactions: [],
      timestamp,
      replyToId: replyToId || undefined,
      reply_to_id: replyToId || undefined,
      mentions: mentions && mentions.length > 0 ? mentions : undefined,
    };

    set((s) => ({
      messages: {
        ...s.messages,
        [discussionId]: [
          ...(s.messages[discussionId] || []),
          optimisticMsg,
        ],
      },
    }));

    try {
      let finalAttachment = attachment;
      if (file) {
        const uploaded = await messagerieApi.uploadAttachment(discussionId, file);
        finalAttachment = {
          type: file.type.startsWith("image/") ? "image" : "file",
          name: uploaded.name,
          gcs_path: uploaded.gcs_path,
          mime_type: uploaded.mime_type,
          size: uploaded.size,
        };
      }

      const payload: {
        text?: string;
        attachment?: ChatAttachment;
        reply_to_id?: string;
        mentions?: string[];
        channel?: import("types/messaging").MessageChannel;
      } = {};
      if (text.trim()) payload.text = text.trim();
      if (finalAttachment) payload.attachment = finalAttachment;
      if (replyToId) payload.reply_to_id = replyToId;
      if (mentions && mentions.length > 0) payload.mentions = mentions;
      if (channel) payload.channel = channel;

      const sent = await messagerieApi.sendMessage(discussionId, payload);
      const real = normalizeMessage(sent);
      set((s) => ({
        messages: {
          ...s.messages,
          [discussionId]: (s.messages[discussionId] || []).map((m) =>
            m.id === optimisticId ? real : m,
          ),
        },
      }));
    } catch (err) {
      console.error("Failed to send message:", err);
      set((s) => ({
        messages: {
          ...s.messages,
          [discussionId]: (s.messages[discussionId] || []).filter(
            (m) => m.id !== optimisticId,
          ),
        },
      }));
    } finally {
      set({ isSending: false });
    }
  },

  // ─── Delete message in a specific discussion ─────────────────
  deleteMessageIn: async (discussionId, messageId) => {
    const { currentUserId } = get();
    const msgs = get().messages[discussionId];
    if (!msgs) return;

    const msg = msgs.find((m) => m.id === messageId);
    if (!msg) return;
    const senderId = msg.sender_id || msg.senderId;
    if (senderId !== currentUserId) return;

    set((s) => ({
      messages: {
        ...s.messages,
        [discussionId]: (s.messages[discussionId] || []).map((m) =>
          m.id === messageId
            ? { ...m, deleted: true, text: undefined, attachment: undefined, reactions: [] }
            : m,
        ),
      },
    }));

    try {
      await messagerieApi.deleteMessage(discussionId, messageId);
    } catch (err) {
      console.error("Failed to delete message:", err);
    }
  },

  // ─── Toggle reaction in a specific discussion ────────────────
  toggleReactionIn: async (discussionId, messageId, emoji) => {
    const { currentUserId } = get();
    if (!discussionId) return;

    set((s) => {
      const msgs = s.messages[discussionId] || [];
      return {
        messages: {
          ...s.messages,
          [discussionId]: msgs.map((m) => {
            if (m.id !== messageId) return m;
            const existing = m.reactions.findIndex(
              (r) =>
                r.emoji === emoji &&
                (r.user_id === currentUserId || r.userId === currentUserId),
            );
            const reactions =
              existing >= 0
                ? m.reactions.filter((_, i) => i !== existing)
                : [
                    ...m.reactions,
                    { emoji, userId: currentUserId, user_id: currentUserId },
                  ];
            return { ...m, reactions };
          }),
        },
      };
    });

    try {
      await messagerieApi.toggleReaction(discussionId, messageId, emoji);
    } catch (err) {
      console.error("Failed to toggle reaction:", err);
    }
  },

  // ─── Delete message ──────────────────────────────────────────
  deleteMessage: async (messageId) => {
    const { selectedDiscussionId, currentUserId } = get();
    if (!selectedDiscussionId) return;

    const msgs = get().messages[selectedDiscussionId];
    if (!msgs) return;

    const msg = msgs.find((m) => m.id === messageId);
    if (!msg) return;
    const senderId = msg.sender_id || msg.senderId;
    if (senderId !== currentUserId) return;

    // Optimistic delete
    set((s) => ({
      messages: {
        ...s.messages,
        [selectedDiscussionId]: (s.messages[selectedDiscussionId] || []).map(
          (m) =>
            m.id === messageId
              ? {
                  ...m,
                  deleted: true,
                  text: undefined,
                  attachment: undefined,
                  reactions: [],
                }
              : m,
        ),
      },
    }));

    try {
      await messagerieApi.deleteMessage(selectedDiscussionId, messageId);
    } catch (err) {
      console.error("Failed to delete message:", err);
    }
  },

  // ─── Toggle reaction ─────────────────────────────────────────
  toggleReaction: async (messageId, emoji) => {
    const { selectedDiscussionId, currentUserId } = get();
    if (!selectedDiscussionId) return;

    // Optimistic toggle
    set((s) => {
      const msgs = s.messages[selectedDiscussionId] || [];
      return {
        messages: {
          ...s.messages,
          [selectedDiscussionId]: msgs.map((m) => {
            if (m.id !== messageId) return m;
            const existing = m.reactions.findIndex(
              (r) =>
                r.emoji === emoji &&
                (r.user_id === currentUserId || r.userId === currentUserId),
            );
            const reactions =
              existing >= 0
                ? m.reactions.filter((_, i) => i !== existing)
                : [
                    ...m.reactions,
                    { emoji, userId: currentUserId, user_id: currentUserId },
                  ];
            return { ...m, reactions };
          }),
        },
      };
    });

    try {
      await messagerieApi.toggleReaction(
        selectedDiscussionId,
        messageId,
        emoji,
      );
    } catch (err) {
      console.error("Failed to toggle reaction:", err);
    }
  },

  // ─── Create discussion ───────────────────────────────────────
  createDiscussion: async (name, memberIds, tab) => {
    try {
      const raw = await messagerieApi.createDiscussion({
        tab,
        name,
        member_ids: memberIds,
      });
      const disc = normalizeDiscussion(raw);

      set((s) => ({
        discussions: [disc, ...s.discussions],
        messages: { ...s.messages, [disc.id]: [] },
        selectedDiscussionId: disc.id,
        activeTab: tab,
      }));
      return disc.id;
    } catch (err) {
      console.error("Failed to create discussion:", err);
      return null;
    }
  },

  // ─── Typing indicator ───────────────────────────────────────
  sendTyping: () => {
    const ws = get()._ws;
    const discId = get().selectedDiscussionId;
    if (!ws || !discId || ws.readyState !== WebSocket.OPEN) return;
    ws.send(
      JSON.stringify({
        type: "typing",
        data: { discussion_id: discId },
      }),
    );
  },

  // ─── Derived getters ────────────────────────────────────────
  getFilteredDiscussions: () => {
    const { discussions, activeTab, searchQuery } = get();
    return discussions.filter((d) => {
      // Hide internal entity discussions from sidebar — accessed via ChatPanel tabs
      if (d.type === "internal") return false;
      if (d.tab !== activeTab) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (
          !d.name.toLowerCase().includes(q) &&
          !(d.lastMessage || "").toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  },

  getUnreadCount: (tab) => {
    return get().discussions.filter(
      (d) =>
        d.tab === tab &&
        (d.unread || (d.unread_count != null && d.unread_count > 0)),
    ).length;
  },

  getTotalUnreadCount: () => {
    return Object.values(get().unreadCounts).reduce((sum, c) => sum + c, 0);
  },

  getSelectedDiscussion: () => {
    const { discussions, selectedDiscussionId } = get();
    return discussions.find((d) => d.id === selectedDiscussionId);
  },

  getDiscussionMessages: () => {
    const { messages, selectedDiscussionId } = get();
    if (!selectedDiscussionId) return [];
    return messages[selectedDiscussionId] || [];
  },

  getDiscussionMembers: () => {
    const disc = get().getSelectedDiscussion();
    if (!disc) return [];
    return get().members.filter((m) => disc.members.includes(m.id));
  },

  getOnlineCount: () => {
    const members = get().getDiscussionMembers();
    return members.filter((m) => m.online).length;
  },

  getMember: (id) => {
    return get().members.find((m) => m.id === id);
  },

  getMessageById: (messageId) => {
    const { messages, selectedDiscussionId } = get();
    if (!selectedDiscussionId) return undefined;
    const msgs = messages[selectedDiscussionId];
    return msgs?.find((m) => m.id === messageId);
  },

  getMatchingMembers: (query) => {
    const members = get().getDiscussionMembers();
    const currentUserId = get().currentUserId;
    if (!query) return members.filter((m) => m.id !== currentUserId);
    const q = query.toLowerCase();
    return members.filter(
      (m) => m.id !== currentUserId && m.name.toLowerCase().startsWith(q),
    );
  },
}));
